// @givmo/board — who is expected at a meeting, and the officer's path to recording it.
//
// ══ THE MANUAL PATH IS FIRST-CLASS, NOT A FALLBACK ═════════════════════════════════════════════
//
// The backend says so in its own words: a director who telephones, replies to a paper notice, or
// tells the secretary in the corridor still has an answer on record. Recording on a director's behalf
// stays valid forever — the module's whole premise is that a volunteer can take part without ever
// holding an account, and an RSVP is the first thing they are asked for.
//
// So this is a peer of the director's own answer, not a degraded copy of it. What the surface owes in
// exchange is honesty about which of the two happened.
//
// ══ ⚠ ACTIVE SEATS ONLY, AND IT IS THE OPPOSITE OF THE IN-MEETING SURFACE ══════════════════════
//
// `record_attendance_intent_on_behalf` resolves the seat with `_require_active_member` and answers
// NOT-FOUND for one that has ended: "an answer recorded against a seat that has ended would put a
// fact about a former director into this meeting's expectations." The in-meeting attendance surface
// deliberately breaks that rule — a director removed in the autumn was in the room in the spring.
//
// The split is about which way time runs. An RSVP is about a meeting still AHEAD, so only a seat that
// will be there can answer; attendance is about one that already HAPPENED. `seatsAnsweringFor`
// encodes the first, `votingSeats` the second, and neither is the general case. For the same reason,
// once the meeting has STARTED (CBG-FIX25: its start known and at or before now) no reply is offered
// for recording; the replies already on record stay listed, as the meeting's history.
//
// No role filter: an observer attends meetings. Role gates who may VOTE, which is a different
// surface's rule.
//
// ══ ⚠ AN ENTRY REPLACES WHATEVER STOOD, INCLUDING THE DIRECTOR'S OWN ANSWER ════════════════════
//
// One row exists per seat per meeting (`on_conflict_do_update` on `(meeting_id, board_member_id)`),
// and `recorded_by` moves with it. So an officer entering an answer over a director's own does not
// add a second record — it replaces theirs, and the record then says an officer entered it. That is
// a real consequence rather than an implementation detail, so the dialog NAMES it before the request
// goes out when the seat has already answered for itself.
//
// The backend preserves `first_responded_at` through every change of mind (it IS the fact the
// attention read asks about — has this director ever answered), which is why re-entry is safe to
// offer at all.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractList } from '@givmo/api-client/list';
import {
  isGivmoError,
  type OrgBoardAttendanceIntentOut,
  type BoardAttendanceIntentValue,
  type OrgBoardMemberOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  Dialog,
  ErrorMessage,
  Select,
  Skeleton,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { StatusWord } from '../../components/fine-print';
import { isCommitteeOnlySeat, sortSeats } from '../roster/roster-decode';
import { capacityLine } from '../roster/officer-roles';
import {
  extractAttendanceIntents,
  intentCaptureLine,
  intentsBySeat,
  isDirectorOwnIntent,
  meetingHasStarted,
  seatsAnsweringFor,
  shortDate,
} from './meetings-decode';
import { useMinuteClock } from '../../lib/minute-clock';
import { useMeetingInvitations, useRefreshCalendar } from '../../calendar/calendar-queries';
import { calendarWords, providerName } from '../../calendar/calendar-words';
import { failureLine } from '../calendar/account-dialogs';
import { isKeptInCalendar, type MeetingCalendar } from './calendar-decode';
import { useBoardOperations } from '../../board-runtime';
import { useBoardPermission } from '../../board-host';
import { committeeInviteeSeatIds } from '../committees/committees-decode';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

const INTENT_OPTIONS: readonly { value: BoardAttendanceIntentValue; label: string }[] = [
  { value: 'attending', label: 'They’ll be there' },
  { value: 'tentative', label: 'They’re not sure' },
  { value: 'not_attending', label: 'They can’t make it' },
];

/** How an answer reads on the officer's own surface — third person, unlike the director's own. */
function intentLabel(value: string | null | undefined): string {
  switch ((value ?? '').toLowerCase()) {
    case 'attending':
      return 'Coming';
    case 'not_attending':
      return 'Not coming';
    case 'tentative':
      return 'Not sure';
    default:
      return 'Replied';
  }
}

/**
 * When the standing reply was given, in its capture class's OWN words (plan §3.7): a director's reply
 * from their link reads "Answered Sep 8", an officer's entry "Recorded for them Sep 10". The two ways a
 * reply is captured never share a label.
 */
function replyWhen(answer: OrgBoardAttendanceIntentOut): string {
  const on = shortDate(answer.responded_at);
  if (answer.recorded_by === 'organization_admin') return `Recorded for them ${on}`;
  if (answer.recorded_by === 'director') return `Answered ${on}`;
  return `${intentCaptureLine(answer)} ${on}`;
}

export interface AttendanceIntentPanelProps {
  orgRef: string;
  meetingId: string;
  /**
   * A called-off meeting offers nothing here. There is nothing to answer for, and an intent to
   * attend something that was called off is a misleading fact in the chair's view — the reason the
   * DIRECTOR's own route refuses one outright. ⚠ The officer's route does NOT refuse it (it checks
   * the meeting exists and the seat is active, and stops), so this is the module declining to offer
   * something the API would accept rather than a gate being mirrored.
   */
  cancelled: boolean;
  /**
   * The meeting's start. Once it has passed (`meetingHasStarted`, read on the minute clock and at the
   * start itself) nobody is asked whether they can make it, so recording a reply is not offered.
   * Absent or unknown: not started.
   */
  startsAt?: string | null;
  /**
   * The meeting's calendar block (CBG-CAL1), or null. Replies directors give in their own calendar
   * are read only for a meeting Board keeps in a CONNECTED calendar: a Givmo-sent meeting's replies
   * go to an address nobody reads (D-CBG-13), and a detached one is no longer kept in step.
   */
  calendar?: MeetingCalendar | null;
  /**
   * CBG-YR6-MEET: a committee's meeting invites the committee's members only (the backend refuses a
   * reply for anyone else), so its rows are theirs. Null for the board's own meeting.
   */
  committeeId?: string | null;
}

/** A calendar reply in the officer's words, the shipped replies' own (`intentLabel`). */
function calendarReplyIntent(response: string | null): string | null {
  switch (response) {
    case 'accepted':
      return 'attending';
    case 'declined':
      return 'not_attending';
    case 'tentative':
      return 'tentative';
    default:
      return null;
  }
}

/**
 * "Who's coming" (CBG-UX1, Jonathan's approved mockup m2/m3): one line saying what it is, a row per
 * serving seat — the reply as a plain word, when it was given in its capture class's own words, and
 * the officer's "Record reply" / "Change" — and, small in the footer, the count of replies and that
 * this is not the attendance record. Replies are plain words with no colour: nobody must act on them.
 */
export function AttendanceIntentPanel({
  orgRef,
  meetingId,
  cancelled,
  startsAt = null,
  calendar = null,
  committeeId = null,
}: AttendanceIntentPanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  // Recording an answer on a director's behalf is `board_meeting:write`; without it, no control.
  const canRecord = useBoardPermission('board_meeting:write');
  const now = useMinuteClock(startsAt);
  const offersRecording = canRecord && !cancelled && !meetingHasStarted(startsAt, now);
  const [recordingFor, setRecordingFor] = useState<OrgBoardMemberOut | null>(null);

  const intentsOptions = operations.officerListBoardAttendanceIntentsOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const intentsQuery = useQuery({
    ...intentsOptions,
    select: extractAttendanceIntents,
  });

  const seatsQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }),
    select: (body: unknown) => sortSeats(extractList<OrgBoardMemberOut>(body, 'data')),
  });
  const committeeQuery = useQuery({
    ...operations.officerGetBoardCommitteeOptions({
      path: { org_ref: orgRef, committee_id: committeeId ?? '' },
    }),
    enabled: committeeId !== null,
  });
  // A committee's meeting: its members' rows only, none until the committee is read.
  const committeeSeats =
    committeeId === null
      ? null
      : committeeQuery.data
        ? committeeInviteeSeatIds(committeeQuery.data)
        : new Set<string>();

  // CBG-CAL1: the third source — a director's reply in their own calendar ("Replied in their
  // calendar Oct 2"), beside the two Board captures. One line per director, the LATEST answer wins
  // (question 3); a calendar reply is never written into Board's own record. A director taken off the
  // invitation in the calendar reads "No invitation", with Invite again (Board never re-adds one).
  const connectedCalendar =
    calendar !== null && calendar.channel === 'org_calendar' && isKeptInCalendar(calendar);
  const invitationsQuery = useMeetingInvitations(orgRef, meetingId, connectedCalendar);
  const refreshCalendar = useRefreshCalendar(orgRef);
  const provider = providerName(calendar?.provider);
  // Only for a connected calendar: the same read is cached for the calendar file's rule on any kept
  // meeting, and a Givmo-sent meeting's replies go to an address nobody reads.
  const invitees = connectedCalendar ? (invitationsQuery.data?.invitees ?? []) : [];
  const liveInvitee = new Map(
    invitees.filter((row) => row.removed_at === null).map((row) => [row.board_member_id, row]),
  );
  const removedInCalendar = new Map(
    invitees
      .filter(
        (row) =>
          row.removed_reason === 'removed_in_calendar' && !liveInvitee.has(row.board_member_id),
      )
      .map((row) => [row.board_member_id, row]),
  );
  const answers = intentsBySeat(intentsQuery.data ?? []);
  /** The seat's calendar reply, when it is newer than the answer Board holds. */
  const calendarReplyFor = (seatId: string) => {
    const invitee = liveInvitee.get(seatId);
    const intent = calendarReplyIntent(invitee?.calendar_response ?? null);
    if (invitee === undefined || intent === null || invitee.calendar_responded_at === null) {
      return null;
    }
    const answer = answers.get(seatId);
    const at = Date.parse(invitee.calendar_responded_at);
    if (answer !== undefined && Date.parse(answer.responded_at ?? '') >= at) return null;
    return { intent, at: invitee.calendar_responded_at };
  };
  const reinvite = useMutation({
    mutationFn: async (memberId: string) => {
      await operations.officerReinviteBoardMeetingInvitee({
        path: { org_ref: orgRef, meeting_id: meetingId, member_id: memberId },
        throwOnError: true,
      });
    },
    onSuccess: () => refreshCalendar(meetingId),
    onError: (error) => toast.error(failureLine(error)),
  });

  // The board's meeting: the board's seats, never someone who isn't on it (CBG-YR6-OUTSIDE, edge 12).
  const seats = seatsAnsweringFor(seatsQuery.data ?? []).filter((seat) =>
    committeeSeats === null ? !isCommitteeOnlySeat(seat) : committeeSeats.has(seat.id),
  );
  const replied = seats.filter(
    (seat) => answers.has(seat.id) || calendarReplyFor(seat.id) !== null,
  ).length;

  return (
    <Card testId="meeting-intents">
      <div className="flex flex-col gap-4 border-b border-border-subtle px-20 py-16">
        <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          Who’s coming
        </h2>
        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* Recording a reply is offered — and described — only to someone who can. The list is
              every seat serving on the board, directors or not, as the roster calls them
              (CBG-FIX42, design-225's rows 3.27 and 3.28, approved 2026-10-17 22:51 PDT). */}
          {committeeId !== null
            ? cm.committeeReplies(offersRecording)
            : offersRecording
              ? 'The board members’ replies. You can record a reply someone gave you in person.'
              : 'The board members’ replies.'}
        </p>
      </div>

      {intentsQuery.isPending ||
      seatsQuery.isPending ||
      (committeeId !== null && committeeQuery.isPending) ? (
        <div className="p-20">
          <Skeleton className="h-84 w-full rounded-lg" testId="meeting-intents-loading" />
        </div>
      ) : null}

      {intentsQuery.isError || seatsQuery.isError || committeeQuery.isError ? (
        <div
          className="flex flex-col items-start gap-10 px-20 py-16"
          data-testid="meeting-intents-error"
        >
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            We couldn’t load the replies just now.
          </p>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => {
              void intentsQuery.refetch();
              void seatsQuery.refetch();
              if (committeeId !== null) void committeeQuery.refetch();
            }}
            testId="meeting-intents-retry"
          >
            Try again
          </Button>
        </div>
      ) : null}

      {seatsQuery.isSuccess && seats.length === 0 ? (
        <p
          className="px-20 py-16 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
          data-testid="meeting-intents-empty"
        >
          Nobody is currently serving on this board, so there is no one to ask.
        </p>
      ) : null}

      {seats.length > 0 ? (
        <ul className="divide-y divide-border-subtle">
          {seats.map((seat) => {
            const answer = answers.get(seat.id) ?? null;
            const fromCalendar = calendarReplyFor(seat.id);
            const removed = removedInCalendar.get(seat.id) ?? null;
            return (
              // From `sm` the row is a table row — who, the reply, when, and (for an officer) the
              // action at the far right — so the reply sits in the same column on every row.
              <li
                key={seat.id}
                className={
                  offersRecording
                    ? 'flex flex-col gap-6 px-20 py-12 sm:grid sm:grid-cols-[minmax(0,224px)_112px_minmax(0,1fr)_auto] sm:items-center sm:gap-x-16'
                    : 'flex flex-col gap-6 px-20 py-12 sm:grid sm:grid-cols-[minmax(0,224px)_112px_minmax(0,1fr)] sm:items-center sm:gap-x-16'
                }
                data-testid={`meeting-intent-${seat.id}`}
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {seat.display_name}
                  </span>
                  <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                    {/* The person's facts, in the one helper's words (CBG-YR3-ROLES layer 1). */}
                    {capacityLine(seat)}
                  </span>
                  {answer?.note ? (
                    <span
                      className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                      data-testid={`meeting-intent-note-${seat.id}`}
                    >
                      {answer.note}
                    </span>
                  ) : null}
                </div>
                {/* Phone: one line — the reply, when, and the action at the right. */}
                <div className="flex items-baseline gap-12 sm:contents">
                  <span className="leading-[20px]">
                    {removed !== null ? (
                      <StatusWord tone="quiet" testId={`meeting-intent-value-${seat.id}`}>
                        {calendarWords.noInvitation}
                      </StatusWord>
                    ) : fromCalendar !== null ? (
                      <StatusWord testId={`meeting-intent-value-${seat.id}`}>
                        {intentLabel(fromCalendar.intent)}
                      </StatusWord>
                    ) : answer !== null ? (
                      <StatusWord testId={`meeting-intent-value-${seat.id}`}>
                        {intentLabel(answer.intent)}
                      </StatusWord>
                    ) : (
                      <StatusWord tone="quiet" testId={`meeting-intent-value-${seat.id}`}>
                        No reply yet
                      </StatusWord>
                    )}
                  </span>
                  {/* The grid keeps the cell; a row with no reply has no capture line in it. */}
                  <span
                    className="font-sans text-s13 font-w400 leading-[20px] tracking-givmo text-text-tertiary"
                    data-testid={
                      answer !== null || fromCalendar !== null || removed !== null
                        ? `meeting-intent-capture-${seat.id}`
                        : undefined
                    }
                  >
                    {/* ⚠ The capture classes never share a label; the calendar is the third. */}
                    {removed !== null
                      ? calendarWords.removedInCalendar(provider, shortDate(removed.removed_at))
                      : fromCalendar !== null
                        ? calendarWords.repliedInCalendar(shortDate(fromCalendar.at))
                        : answer !== null
                          ? replyWhen(answer)
                          : null}
                  </span>
                  {offersRecording ? (
                    <span className="ml-auto leading-[20px] sm:ml-0 sm:text-right">
                      {removed !== null ? (
                        <Button
                          appearance="saas"
                          tone="link"
                          size="sm"
                          loading={reinvite.isPending && reinvite.variables === seat.id}
                          onClick={() => reinvite.mutate(seat.id)}
                          testId={`meeting-intent-invite-again-${seat.id}`}
                        >
                          {calendarWords.inviteAgain}
                        </Button>
                      ) : (
                        <Button
                          appearance="saas"
                          tone="link"
                          size="sm"
                          onClick={() => setRecordingFor(seat)}
                          testId={`meeting-intent-record-${seat.id}`}
                        >
                          {answer === null && fromCalendar === null ? 'Record reply' : 'Change'}
                        </Button>
                      )}
                    </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {seats.length > 0 ? (
        <div
          className="border-t border-border-subtle px-20 py-12 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
          data-testid="meeting-intents-count"
        >
          {/* A count of the REPLIES, never a prediction of the room and never compared against a
              quorum figure — whether the board can act is decided at the meeting, on the seats
              actually present. */}
          {replied} of {seats.length} have replied · This isn’t the attendance record.
        </div>
      ) : null}

      {recordingFor !== null ? (
        <RecordIntentDialog
          orgRef={orgRef}
          meetingId={meetingId}
          seat={recordingFor}
          current={answers.get(recordingFor.id) ?? null}
          onClose={() => setRecordingFor(null)}
          onRecorded={() => {
            setRecordingFor(null);
            void queryClient.invalidateQueries({ queryKey: intentsOptions.queryKey });
          }}
        />
      ) : null}
    </Card>
  );
}

/** Recording one seat's answer on their behalf. The panel's own dialog; no other caller. */
function RecordIntentDialog({
  orgRef,
  meetingId,
  seat,
  current,
  onClose,
  onRecorded,
}: {
  orgRef: string;
  meetingId: string;
  seat: OrgBoardMemberOut;
  /** What stands for this seat today, or null. Drives the replacement warning. */
  current: OrgBoardAttendanceIntentOut | null;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardOperations();
  const [intent, setIntent] = useState<BoardAttendanceIntentValue>(
    (current?.intent as BoardAttendanceIntentValue | undefined) ?? 'attending',
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardAttendanceIntentOnBehalf({
        path: { org_ref: orgRef, meeting_id: meetingId, member_id: seat.id },
        body: {
          intent,
          // Omitted rather than blanked when the officer added nothing — the field is how the
          // organization records HOW it was told, and an empty string is not that.
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recorded.');
      onRecorded();
    },
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      ),
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !record.isPending && onClose()}>
      <Dialog.Content
        aria-label="Record an answer on this member's behalf"
        className="flex flex-col gap-16"
        testId="meeting-intent-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record {seat.display_name}’s answer
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          This records that <em>you</em> entered their answer, with your name against it — not that
          they answered from their own link. Both count the same; the record just says which
          happened.
        </p>

        {isDirectorOwnIntent(current) ? (
          // ⚠ THE REPLACEMENT WARNING. One row exists per seat per meeting, so this does not add a
          // second answer beside theirs — it takes the place of the one they gave, and the record
          // then names an officer instead of them.
          <FieldNote testId="meeting-intent-replaces">
            {seat.display_name} has already answered from their own link. Recording here replaces
            that answer, and the record will show that you entered it.
          </FieldNote>
        ) : null}

        <Select
          label="What they said"
          appearance="saas"
          value={intent}
          onValueChange={(next) => next !== null && setIntent(next as BoardAttendanceIntentValue)}
          options={INTENT_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          testId="meeting-intent-value"
        />

        <Textarea
          label="How you were told (optional)"
          rows={3}
          value={note}
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
          testId="meeting-intent-note"
        />
        <FieldNote>
          Worth a line when it matters later — “telephoned on the 3rd”, “replied to the posted
          notice”.
        </FieldNote>

        {error !== null ? <ErrorMessage testId="meeting-intent-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={record.isPending}
            onClick={onClose}
            testId="meeting-intent-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="meeting-intent-confirm"
          >
            Record it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
